import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditAnalytics = ({ className = '' }) => {
  const [activeTab, setActiveTab] = useState('activity');

  // Mock analytics data
  const activityData = [
    { name: 'Mon', testResults: 45, equipmentCal: 12, adverseEvents: 3, regulatory: 8 },
    { name: 'Tue', testResults: 52, equipmentCal: 15, adverseEvents: 5, regulatory: 6 },
    { name: 'Wed', testResults: 38, equipmentCal: 8, adverseEvents: 2, regulatory: 12 },
    { name: 'Thu', testResults: 61, equipmentCal: 18, adverseEvents: 7, regulatory: 9 },
    { name: 'Fri', testResults: 48, equipmentCal: 14, adverseEvents: 4, regulatory: 15 },
    { name: 'Sat', testResults: 23, equipmentCal: 6, adverseEvents: 1, regulatory: 3 },
    { name: 'Sun', testResults: 18, equipmentCal: 4, adverseEvents: 2, regulatory: 2 }
  ];

  const userActivityData = [
    { name: 'QA Managers', value: 35, count: 156 },
    { name: 'Lab Technicians', value: 28, count: 124 },
    { name: 'Regulatory Officers', value: 18, count: 78 },
    { name: 'Pharmacovigilance', value: 12, count: 52 },
    { name: 'Lab Directors', value: 7, count: 31 }
  ];

  const verificationTrendData = [
    { time: '00:00', verified: 98.5, pending: 1.2, failed: 0.3 },
    { time: '04:00', verified: 97.8, pending: 1.8, failed: 0.4 },
    { time: '08:00', verified: 99.1, pending: 0.7, failed: 0.2 },
    { time: '12:00', verified: 98.9, pending: 0.9, failed: 0.2 },
    { time: '16:00', verified: 99.3, pending: 0.5, failed: 0.2 },
    { time: '20:00', verified: 98.7, pending: 1.1, failed: 0.2 }
  ];

  const COLORS = ['#1E40AF', '#059669', '#F59E0B', '#DC2626', '#64748B'];

  const tabs = [
    { id: 'activity', label: 'Activity Overview', icon: 'BarChart3' },
    { id: 'users', label: 'User Analytics', icon: 'Users' },
    { id: 'verification', label: 'Verification Trends', icon: 'Shield' },
    { id: 'compliance', label: 'Compliance Metrics', icon: 'CheckCircle' }
  ];

  const renderActivityChart = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-card-foreground">Weekly Activity Distribution</h4>
        <Button variant="outline" size="sm" iconName="Download">
          Export
        </Button>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={activityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
            <YAxis stroke="#6B7280" fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E5E7EB',
                borderRadius: '6px'
              }}
            />
            <Bar dataKey="testResults" stackId="a" fill="#1E40AF" name="Test Results" />
            <Bar dataKey="equipmentCal" stackId="a" fill="#059669" name="Equipment Cal." />
            <Bar dataKey="adverseEvents" stackId="a" fill="#F59E0B" name="Adverse Events" />
            <Bar dataKey="regulatory" stackId="a" fill="#DC2626" name="Regulatory" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderUserChart = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-card-foreground">User Activity Distribution</h4>
        <Button variant="outline" size="sm" iconName="Download">
          Export
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={userActivityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {userActivityData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3">
          {userActivityData?.map((item, index) => (
            <div key={item?.name} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                />
                <span className="text-sm font-medium text-card-foreground">{item?.name}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-card-foreground">{item?.count}</div>
                <div className="text-xs text-muted-foreground">{item?.value}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderVerificationChart = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-card-foreground">Blockchain Verification Trends</h4>
        <Button variant="outline" size="sm" iconName="Download">
          Export
        </Button>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={verificationTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
            <YAxis stroke="#6B7280" fontSize={12} domain={[95, 100]} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF', 
                border: '1px solid #E5E7EB',
                borderRadius: '6px'
              }}
              formatter={(value) => [`${value}%`, '']}
            />
            <Line 
              type="monotone" 
              dataKey="verified" 
              stroke="#059669" 
              strokeWidth={2}
              name="Verified"
            />
            <Line 
              type="monotone" 
              dataKey="pending" 
              stroke="#F59E0B" 
              strokeWidth={2}
              name="Pending"
            />
            <Line 
              type="monotone" 
              dataKey="failed" 
              stroke="#DC2626" 
              strokeWidth={2}
              name="Failed"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderComplianceMetrics = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-card-foreground">Compliance Metrics</h4>
        <Button variant="outline" size="sm" iconName="Download">
          Export
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Data Integrity</span>
          </div>
          <div className="text-2xl font-bold text-success">99.8%</div>
          <div className="text-xs text-muted-foreground">Last 30 days</div>
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Audit Coverage</span>
          </div>
          <div className="text-2xl font-bold text-primary">100%</div>
          <div className="text-xs text-muted-foreground">All activities logged</div>
        </div>

        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">Response Time</span>
          </div>
          <div className="text-2xl font-bold text-warning">2.3s</div>
          <div className="text-xs text-muted-foreground">Avg verification time</div>
        </div>

        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="FileText" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Reports Generated</span>
          </div>
          <div className="text-2xl font-bold text-accent">247</div>
          <div className="text-xs text-muted-foreground">This month</div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <h5 className="font-medium text-card-foreground mb-3">Recent Compliance Events</h5>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
            <span className="text-sm text-card-foreground">FDA Audit Trail Export</span>
            <span className="text-xs text-success">Completed</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
            <span className="text-sm text-card-foreground">EMA Compliance Review</span>
            <span className="text-xs text-warning">In Progress</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
            <span className="text-sm text-card-foreground">ISO 17025 Documentation</span>
            <span className="text-xs text-primary">Scheduled</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'activity': return renderActivityChart();
      case 'users': return renderUserChart();
      case 'verification': return renderVerificationChart();
      case 'compliance': return renderComplianceMetrics();
      default: return renderActivityChart();
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          <h3 className="font-medium text-card-foreground">Audit Analytics</h3>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-1 p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Content */}
      <div className="p-4">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AuditAnalytics;